/**
 * Onboarding Page - Payment-First User Registration
 *
 * Flow:
 * 1. User arrives after completing payment on ChargeKeep
 * 2. User confirms email to verify payment
 * 3. User creates password
 * 4. User is logged in automatically
 *
 * Uses PARACLETE dark theme design tokens
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import {
  verifyPayment,
  completeOnboarding,
  VerifyPaymentResponse,
} from '../api/onboarding';

// =============================================================================
// Types
// =============================================================================

type OnboardingStep = 'email' | 'password' | 'success';

interface PasswordValidation {
  minLength: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasNumber: boolean;
  passwordsMatch: boolean;
}

// =============================================================================
// Password Requirements Component
// =============================================================================

interface PasswordRequirementsProps {
  password: string;
  confirmPassword: string;
  validation: PasswordValidation;
}

const PasswordRequirements: React.FC<PasswordRequirementsProps> = ({
  password,
  validation,
}) => {
  const requirements = [
    { label: 'At least 8 characters', met: validation.minLength },
    { label: 'One uppercase letter (A-Z)', met: validation.hasUppercase },
    { label: 'One lowercase letter (a-z)', met: validation.hasLowercase },
    { label: 'One number (0-9)', met: validation.hasNumber },
  ];

  return (
    <div className="mt-4 p-4 bg-dark-bg/50 rounded-lg border border-dark-border">
      <h4 className="text-sm font-medium text-dark-text mb-3">Password Requirements</h4>
      <ul className="space-y-2">
        {requirements.map((req, index) => (
          <li key={index} className="flex items-center gap-2 text-sm">
            <span
              className={`w-5 h-5 flex items-center justify-center rounded-full text-xs ${
                password.length > 0 && req.met
                  ? 'bg-green-500/20 text-green-400'
                  : 'bg-dark-border text-dark-text-muted'
              }`}
            >
              {password.length > 0 && req.met ? '✓' : '○'}
            </span>
            <span
              className={
                password.length > 0 && req.met
                  ? 'text-green-400'
                  : 'text-dark-text-muted'
              }
            >
              {req.label}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-dark-text-muted">
        Special characters are optional but allowed.
      </p>
    </div>
  );
};

// =============================================================================
// Step Indicator Component
// =============================================================================

interface StepIndicatorProps {
  currentStep: OnboardingStep;
}

const StepIndicator: React.FC<StepIndicatorProps> = ({ currentStep }) => {
  const steps = [
    { key: 'email', label: 'Verify Email' },
    { key: 'password', label: 'Create Password' },
    { key: 'success', label: 'Complete' },
  ];

  const currentIndex = steps.findIndex((s) => s.key === currentStep);

  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {steps.map((step, index) => (
        <React.Fragment key={step.key}>
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
              index <= currentIndex
                ? 'bg-accent-blue/20 text-accent-blue'
                : 'bg-dark-border text-dark-text-muted'
            }`}
          >
            <span
              className={`w-5 h-5 flex items-center justify-center rounded-full text-xs font-medium ${
                index < currentIndex
                  ? 'bg-green-500 text-white'
                  : index === currentIndex
                  ? 'bg-accent-blue text-white'
                  : 'bg-dark-border text-dark-text-muted'
              }`}
            >
              {index < currentIndex ? '✓' : index + 1}
            </span>
            <span className="hidden sm:inline">{step.label}</span>
          </div>
          {index < steps.length - 1 && (
            <div
              className={`w-8 h-0.5 ${
                index < currentIndex ? 'bg-accent-blue' : 'bg-dark-border'
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// =============================================================================
// Main Onboarding Page Component
// =============================================================================

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loadUser } = useAuthStore();

  // State
  const [step, setStep] = useState<OnboardingStep>('email');
  const [email, setEmail] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [subscriptionTier, setSubscriptionTier] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Get tier and period from URL params (set by ChargeKeep redirect)
  const tierFromUrl = searchParams.get('tier') || 'basic';
  const periodFromUrl = searchParams.get('period') || 'monthly';

  // Pre-fill email from URL if provided
  useEffect(() => {
    const urlEmail = searchParams.get('email');
    if (urlEmail) {
      setEmail(urlEmail);
    }
  }, [searchParams]);

  // Password validation
  const passwordValidation = useMemo<PasswordValidation>(() => ({
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
    passwordsMatch: password === confirmPassword && password.length > 0,
  }), [password, confirmPassword]);

  const isPasswordValid = useMemo(() => {
    return (
      passwordValidation.minLength &&
      passwordValidation.hasUppercase &&
      passwordValidation.hasLowercase &&
      passwordValidation.hasNumber &&
      passwordValidation.passwordsMatch
    );
  }, [passwordValidation]);

  // =============================================================================
  // Handlers
  // =============================================================================

  const handleVerifyEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Pass tier and period from URL params to verify endpoint
      const response: VerifyPaymentResponse = await verifyPayment(email, tierFromUrl, periodFromUrl);

      if (response.verified && response.verification_token) {
        setVerificationToken(response.verification_token);
        setSubscriptionTier(response.subscription_tier || 'basic');
        setStep('password');
      } else {
        setError(response.message || 'Unable to verify payment. Please try again.');
      }
    } catch (err: any) {
      const message = err?.response?.data?.detail || err?.message || 'Verification failed';
      setError(typeof message === 'string' ? message : 'Verification failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isPasswordValid) {
      setError('Please ensure all password requirements are met.');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await completeOnboarding(
        verificationToken,
        password,
        confirmPassword,
        fullName || undefined,
        smsOptIn
      );

      if (response.success && response.access_token) {
        // Store auth tokens in localStorage (same as login flow)
        localStorage.setItem('access_token', response.access_token);
        if (response.refresh_token) {
          localStorage.setItem('refresh_token', response.refresh_token);
        }

        // Load user to update auth state
        await loadUser();

        // Show success briefly then redirect
        setStep('success');
        setTimeout(() => {
          navigate('/');
        }, 2000);
      } else {
        setError(response.message || 'Account creation failed. Please try again.');
      }
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      let message = 'Account creation failed. Please try again.';

      if (typeof detail === 'string') {
        message = detail;
      } else if (Array.isArray(detail)) {
        // Validation errors from Pydantic
        message = detail.map((d: any) => d.msg).join('. ');
      } else if (detail?.message) {
        message = detail.message;
      }

      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  // =============================================================================
  // Render
  // =============================================================================

  return (
    <div className="min-h-screen bg-dark-bg flex flex-col items-center justify-center p-4">
      {/* Background gradient effect */}
      <div className="fixed inset-0 bg-gradient-to-br from-accent-blue/5 via-transparent to-accent-orange/5 pointer-events-none" />

      {/* Main card */}
      <div className="relative w-full max-w-md">
        {/* Logo/Brand */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-dark-text mb-2">PARACLETE</h1>
          <p className="text-dark-text-muted">AI-Powered Ministry Assistant</p>
        </div>

        {/* Glassmorphism card */}
        <div className="bg-dark-card/95 backdrop-blur-xl border border-dark-border rounded-2xl p-8 shadow-2xl">
          {/* Step indicator */}
          <StepIndicator currentStep={step} />

          {/* Error message */}
          {error && (
            <div
              className="mb-6 p-4 bg-red-900/30 border border-red-500/50 rounded-lg"
              role="alert"
            >
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Step: Email Verification */}
          {step === 'email' && (
            <form onSubmit={handleVerifyEmail} className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-dark-text mb-2">
                  Welcome to PARACLETE!
                </h2>
                <p className="text-dark-text-muted text-sm">
                  Thank you for your payment. Please confirm your email address to
                  continue setting up your account.
                </p>
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-dark-text mb-2"
                >
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter the email you used for payment"
                  className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-dark-text placeholder-dark-text-muted focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-all"
                  required
                  autoFocus
                  autoComplete="email"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || !email}
                className="w-full py-3 px-4 bg-accent-blue hover:bg-accent-blue/90 text-white font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-accent-blue focus:ring-offset-2 focus:ring-offset-dark-card"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg
                      className="animate-spin h-5 w-5"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Verifying...
                  </span>
                ) : (
                  'Verify My Payment'
                )}
              </button>

              <p className="text-center text-sm text-dark-text-muted">
                Already have an account?{' '}
                <a
                  href="/login"
                  className="text-accent-blue hover:text-accent-blue/80 font-medium"
                >
                  Sign in
                </a>
              </p>
            </form>
          )}

          {/* Step: Password Creation */}
          {step === 'password' && (
            <form onSubmit={handleCreateAccount} className="space-y-6">
              <div className="text-center mb-6">
                <h2 className="text-xl font-semibold text-dark-text mb-2">
                  Create Your Password
                </h2>
                <p className="text-dark-text-muted text-sm">
                  Payment verified! You're signing up for the{' '}
                  <span className="text-accent-blue font-medium uppercase">
                    {subscriptionTier}
                  </span>{' '}
                  plan.
                </p>
              </div>

              {/* Full Name (optional) */}
              <div>
                <label
                  htmlFor="fullName"
                  className="block text-sm font-medium text-dark-text mb-2"
                >
                  Full Name <span className="text-dark-text-muted">(optional)</span>
                </label>
                <input
                  type="text"
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Your name"
                  className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-dark-text placeholder-dark-text-muted focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-all"
                  autoComplete="name"
                />
              </div>

              {/* SMS Notifications Opt-in (10DLC Compliance) */}
              <div className="flex items-start gap-3 p-4 bg-dark-bg/50 rounded-lg border border-dark-border">
                <input
                  type="checkbox"
                  id="smsOptIn"
                  checked={smsOptIn}
                  onChange={(e) => setSmsOptIn(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-dark-border bg-dark-bg text-accent-blue focus:ring-accent-blue focus:ring-offset-0"
                />
                <label htmlFor="smsOptIn" className="text-sm text-dark-text-muted">
                  <span className="font-medium text-dark-text">Receive SMS notifications</span>
                  <p className="mt-1">
                    I agree to receive transactional SMS notifications including verification codes,
                    appointment reminders, and account alerts from PARACLETE. Message & data rates may apply.
                    Reply STOP to cancel. View our{' '}
                    <a href="https://ministry.builders/privacy" className="text-accent-blue hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                    {' '}and{' '}
                    <a href="https://ministry.builders/terms" className="text-accent-blue hover:underline" target="_blank" rel="noopener noreferrer">Terms of Service</a>.
                  </p>
                </label>
              </div>

              {/* Password */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-medium text-dark-text mb-2"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Create a secure password"
                    className="w-full px-4 py-3 bg-dark-bg border border-dark-border rounded-lg text-dark-text placeholder-dark-text-muted focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-all pr-12"
                    required
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-text-muted hover:text-dark-text p-1"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-medium text-dark-text mb-2"
                >
                  Confirm Password
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className={`w-full px-4 py-3 bg-dark-bg border rounded-lg text-dark-text placeholder-dark-text-muted focus:outline-none focus:ring-2 focus:ring-accent-blue focus:border-transparent transition-all ${
                    confirmPassword.length > 0 && !passwordValidation.passwordsMatch
                      ? 'border-red-500'
                      : 'border-dark-border'
                  }`}
                  required
                  autoComplete="new-password"
                />
                {confirmPassword.length > 0 && !passwordValidation.passwordsMatch && (
                  <p className="mt-1 text-sm text-red-400">Passwords do not match</p>
                )}
              </div>

              {/* Password Requirements */}
              <PasswordRequirements
                password={password}
                confirmPassword={confirmPassword}
                validation={passwordValidation}
              />

              <button
                type="submit"
                disabled={isLoading || !isPasswordValid}
                className="w-full py-3 px-4 bg-accent-blue hover:bg-accent-blue/90 text-white font-medium rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-accent-blue focus:ring-offset-2 focus:ring-offset-dark-card"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg
                      className="animate-spin h-5 w-5"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    Creating Account...
                  </span>
                ) : (
                  'Complete Setup'
                )}
              </button>

              <button
                type="button"
                onClick={() => setStep('email')}
                className="w-full py-2 text-dark-text-muted hover:text-dark-text text-sm transition-colors"
              >
                Back to email verification
              </button>
            </form>
          )}

          {/* Step: Success */}
          {step === 'success' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <svg
                  className="w-8 h-8 text-green-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-dark-text mb-2">
                Welcome to PARACLETE!
              </h2>
              <p className="text-dark-text-muted mb-6">
                Your account has been created successfully. Redirecting you to the
                dashboard...
              </p>
              <div className="flex items-center justify-center gap-2 text-accent-blue">
                <svg
                  className="animate-spin h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                <span>Loading dashboard...</span>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-dark-text-muted text-sm mt-8">
          Need help?{' '}
          <a
            href="mailto:support@paraclete.ai"
            className="text-accent-blue hover:text-accent-blue/80"
          >
            Contact Support
          </a>
        </p>
      </div>
    </div>
  );
};

export default Onboarding;
